import {
  Box,
  Typography,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from '@mui/material';
import { FC, useState } from 'react';
import { Footer } from '@/components/details/Footer';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import BasicActivityInfo from '@/components/details/BasicActivityInfo';

import { PrivilegeEnum, SampleActivityDto, StatusDto, UpdateSampleActivityRequest } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { log } from '@/lib/utils/logger';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { updateSampleActivityWithPreconditionRetry } from '@/lib/tree/submitEntityUtils';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useSampleActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createSampleActivityBasicSaveValidation } from '@/lib/validation/saveValidationBuilders';

/**
 * Props for the TreeArea component
 */
export const selectOptions: OptionKey[] = ['researcherOptions', 'status', 'visibility'];

const SplitActivityDetails: FC = () => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { resolveUser } = useUserResolution();

  const { createNode, setDataModified, updateDataPartial, updateData } = useTree();
  const { selectedNode } = useTreeSelection();

  // Check if this is a readonly node type
  const isReadOnlyNode = selectedNode ? nodeIsReadOnlyForUi(selectedNode.type) : false;

  const [openDialog, setOpenDialog] = useState(false);
  const [pieceCountInput, setPieceCountInput] = useState('1');

  // Track manual edit mode state (when user clicks "Edit Details" button)
  // This allows entering edit mode even when there are no modifications yet
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  const activity = useSampleActivityData(selectedNode);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  // Form is editable if: user manually entered edit mode OR there are unsaved modifications
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  if (!activity || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      </Box>
    );
  }

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  const isSubmitted = activity?.status === StatusDto.Submitted;

  const handleAddPieces = async () => {
    try {
      const pieceCount = parseInt(pieceCountInput, 10);
      // Send the request to the backend, let it create those pieces in the database
      const newPieces = await backend.sample.addPieces(activity.id, pieceCount);

      // Merge new piece labels into activity immutably (avoid mutating store-backed object in an async handler).
      const nextPieces = [...(activity.pieces || []), ...newPieces.map((p) => p.sampleLabel!)];
      await updateDataPartial(selectedNode.dataId, { pieces: nextPieces });

      for (const piece of newPieces) {
        // Create a new node for each piece, and add to the tree under this node.
        await createNode('sample', selectedNode.nodeId, piece);
      }
      showSnackbar('Sample split successfully', 'success');
    } catch (error) {
      log.error('[SplitActivity] Error splitting sample:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to split sample.', 'error');
    } finally {
      setOpenDialog(false);
    }
  };

  const handleModification = (modified: Partial<SampleActivityDto>) => {
    updateDataPartial(selectedNode.dataId, modified);
  };

  const handleSave = async () => {
    if (!activity.id) return;
    const saveValidation = validateRequiredToSave(createSampleActivityBasicSaveValidation(activity));
    if (!saveValidation.isValid) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    try {
      const request: UpdateSampleActivityRequest = { ...activity };
      const preconditionDeps = { showSnackbar, showConfirmDialog };
      const updated = await updateSampleActivityWithPreconditionRetry(backend, activity.id, request, preconditionDeps);
      if (updated === null) return;
      // Update the data in the store
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after successful save
      showSnackbar('Split activity saved successfully', 'success');
    } catch (error) {
      log.error('[SplitActivity] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save split activity.', 'error');
    }
  };

  // Handler for Footer's setIsReadOnly callback
  // When value === false: user clicked "Edit Details" → enter edit mode
  // When value === true: user clicked "Cancel" → exit edit mode and clear modifications
  const setIsReadOnly = (value: boolean) => {
    setIsManuallyEditing(!value); // false = edit mode, true = read-only mode
    if (value && selectedNode) {
      // Exiting edit mode: clear any unsaved modifications
      setDataModified(selectedNode.dataId, false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <BasicActivityInfo
            activity={activity}
            isReadOnly={isReadOnly}
            isSubmitted={isSubmitted}
            handleModification={handleModification}
            convertDateForBackend={(date) => (date ? new Date(date.toDateString()) : undefined)}
            hideVisibility={isReadOnlyNode}
          />

          {/* Pieces Section */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Nb of pieces
                </Typography>
                <Box sx={{ display: 'flex', width: 'fit-content', borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: 'action.disabledBackground',
                      fontSize: '1rem',
                      minWidth: '2.5rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {activity.pieces?.length ?? 'n/a'}
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    onClick={() => setOpenDialog(true)}
                  >
                    Add another Piece
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Dialog for adding pieces */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
            <DialogTitle>Split Sample</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" gutterBottom>
                Number of additional pieces
              </Typography>
              <TextField
                fullWidth
                value={pieceCountInput}
                onChange={(e) => setPieceCountInput(e.target.value)}
                type="number"
                slotProps={{
                  input: {
                    inputProps: {
                      min: 1,
                    },
                  },
                }}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="contained" color="primary" onClick={handleAddPieces}>
                Add Pieces
              </Button>
              <Button variant="contained" color="error" onClick={() => setOpenDialog(false)}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        {isReadOnlyNode ? (
          <ReadOnlyFooter
            lastChangedDate={activity.lastChangedDate}
            lastChangedBy={activity.lastChangedByUser || 'Unknown'}
            status={activity.status || 'Draft'}
          />
        ) : (
          <Footer
            lastChangedDate={activity.lastChangedDate}
            lastChangedBy={resolveUser(activity.lastChangedByUser || '')}
            status={activity.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={setIsReadOnly}
            onSave={handleSave}
            privilege={PrivilegeEnum.SplitActivity}
            hideDelete /* Temporary: remove once backend delete (ActivitySubSample FK) is fixed */
          />
        )}
      </Box>
    </Box>
  );
};

export default SplitActivityDetails;
